import { useQuery } from "@tanstack/react-query";
import { erc20Abi, formatUnits, type Address } from "viem";

import type { Token } from "@/config/tokens";
import { buildPublicClient } from "@/lib/wallet/clients";

export type TokenBalance = {
  readonly raw: bigint;
  readonly formatted: string;
};

export type TokenBalanceQuery = {
  readonly balance: TokenBalance | null;
  readonly isLoading: boolean;
  readonly error: Error | null;
};

export const useTokenBalance = (
  token: Token,
  account: Address | undefined,
): TokenBalanceQuery => {
  const { data, isLoading, error } = useQuery({
    queryKey: ["balance", token.address, account],
    enabled: Boolean(account),
    queryFn: async (): Promise<TokenBalance> => {
      const client = buildPublicClient();
      const raw = await client.readContract({
        address: token.address,
        abi: erc20Abi,
        functionName: "balanceOf",
        args: [account as Address],
      });
      return { raw, formatted: formatUnits(raw, token.decimals) };
    },
  });
  return {
    balance: data ?? null,
    isLoading,
    error: (error as Error | null) ?? null,
  };
};
